/**
 * Versioned infrastructure price table.
 *
 * This file is the ONLY place infrastructure list prices live in the codebase.
 * In production the control plane publishes the active table to EDGE_CONFIG
 * (`pricing:infra`) so prices change without a deployment; this default is the
 * bootstrap/fallback version.
 *
 * Money is integer micros: `unitPriceMicros` is charged per `perUnits` units
 * beyond `includedAllowance` (allowances are account-wide monthly figures).
 */
export interface InfraPriceRow {
  product: string;
  unit: string;
  unitPriceMicros: number;
  perUnits: number;
  includedAllowance: number;
  tier?: string;
  region?: string;
  effectiveFrom: string;
  effectiveTo: string | null;
}

export interface InfraPriceTable { version: string; currency: "USD"; rows: InfraPriceRow[]; source: string }

const FROM = "2026-10-01T00:00:00Z";
const row = (product: string, unit: string, usd: number, perUnits: number, includedAllowance: number, tier = "workers_paid"): InfraPriceRow => ({
  product, unit, unitPriceMicros: Math.round(usd * 1_000_000), perUnits, includedAllowance, tier, effectiveFrom: FROM, effectiveTo: null,
});
const M = 1_000_000;

export const DEFAULT_INFRA_PRICE_TABLE: InfraPriceTable = {
  version: "cf-2026-10-01",
  currency: "USD",
  source: "Cloudflare list prices cross-checked against developers.cloudflare.com on 2026-10-16 (see docs/cloudflare-decision-record.md); analytics_engine row unverified; verify before billing use. dbindex.* rows are DBIndex platform list prices (placeholder, not yet decided).",
  rows: [
    row("workers.requests", "request", 0.3, M, 10 * M),
    row("workers.cpu_ms", "cpu-ms", 0.02, M, 30 * M),
    row("do.requests", "request", 0.15, M, 1 * M),
    row("do.duration", "GB-s", 12.5, M, 400_000),
    row("do.sqlite.rows_read", "row", 0.001, M, 25_000 * M),
    row("do.sqlite.rows_written", "row", 1.0, M, 50 * M),
    row("do.sqlite.storage", "GB-month", 0.2, 1, 5),
    row("d1.rows_read", "row", 0.001, M, 25_000 * M),
    row("d1.rows_written", "row", 1.0, M, 50 * M),
    row("d1.storage", "GB-month", 0.75, 1, 5),
    row("kv.reads", "read", 0.5, M, 10 * M),
    row("kv.writes", "write", 5.0, M, 1 * M),
    row("kv.storage", "GB-month", 0.5, 1, 1),
    row("r2.storage", "GB-month", 0.015, 1, 10),
    row("r2.class_a", "operation", 4.5, M, 1 * M),
    row("r2.class_b", "operation", 0.36, M, 10 * M),
    row("queues.operations", "operation", 0.4, M, 1 * M),
    row("vectorize.queried_dimensions", "dimension", 0.01, M, 50 * M),
    row("vectorize.stored_dimensions", "dimension", 0.05, 100 * M, 10 * M),
    row("workers_ai.neurons", "neuron", 0.011, 1000, 300_000),
    row("analytics_engine.data_points", "data point", 0.25, M, 10 * M),
    row("dbindex.platform_dataset", "dataset-month", 2.0, 1, 0, "dbindex"),
  ],
};

export interface InfrastructurePricingProvider { current(at?: Date): Promise<InfraPriceTable> }

/** Rows effective at `at`; when several versions of a product overlap, the latest effectiveFrom wins. */
export function effectiveTable(table: InfraPriceTable, at: Date = new Date()): InfraPriceTable {
  const t = at.getTime();
  const best = new Map<string, InfraPriceRow>();
  for (const r of table.rows) {
    if (Date.parse(r.effectiveFrom) > t) continue;
    if (r.effectiveTo && Date.parse(r.effectiveTo) <= t) continue;
    const key = `${r.product}|${r.region ?? ""}|${r.tier ?? ""}`;
    const cur = best.get(key);
    if (!cur || Date.parse(r.effectiveFrom) > Date.parse(cur.effectiveFrom)) best.set(key, r);
  }
  return { ...table, rows: [...best.values()] };
}

export function staticPricingProvider(table: InfraPriceTable): InfrastructurePricingProvider {
  return { current: async (at?: Date) => effectiveTable(table, at) };
}

export function priceRow(table: InfraPriceTable, product: string): InfraPriceRow {
  const r = table.rows.find((x) => x.product === product);
  if (!r) throw new Error(`price table ${table.version} has no row for ${product}`);
  return r;
}

/** Exact (fractional) micros for `quantity` units, ignoring allowances. */
export function costExactMicros(table: InfraPriceTable, product: string, quantity: number): number {
  const r = priceRow(table, product);
  return (Math.max(0, quantity) / r.perUnits) * r.unitPriceMicros;
}
